import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Code, Plus, Search, Copy, Eye, Filter, CheckCircle2, 
  Package, ChevronRight, Sparkles, Library, FileText, X, Pencil, Trash2
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

export default function ItemCodeManagerPage() {
  // Library state
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');

  // Generator state
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [attributeSelections, setAttributeSelections] = useState({});
  const [previewCode, setPreviewCode] = useState('');
  const [previewSpecs, setPreviewSpecs] = useState('');
  const [generating, setGenerating] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);

  // Attribute data for generator
  const [attributeValues, setAttributeValues] = useState({});
  
  // Attribute search state (for filtering large value lists)
  const [attributeSearchTerms, setAttributeSearchTerms] = useState({});

  // View code details dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingCode, setViewingCode] = useState(null);

  // Current user for permission check
  const [currentUser, setCurrentUser] = useState(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({ code: '', generic_name: '', specs: '' });
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleting, setDeleting] = useState(null);

  // Fetch codes library
  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/item-code-generator/codes`, { headers: getAuthHeader() });
      // API returns { total: number, codes: [] }
      setCodes(response.data?.codes || []);
    } catch (error) {
      console.error('Error fetching codes:', error);
      toast.error('Failed to load codes');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch current user for permission checks
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { headers: getAuthHeader() });
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  }, []);

  // Fetch products for generator
  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/item-code-generator/products?is_active=true`, { headers: getAuthHeader() });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchCodes();
    fetchProducts();
    fetchCurrentUser();
  }, [fetchCodes, fetchCurrentUser]);

  // Permission check: Can user edit/delete this code?
  const canEditCode = (code) => {
    if (!currentUser) return false;
    // Owner can edit any code
    if (currentUser.is_owner || currentUser.email === 'dinesh@dreamspan.in') return true;
    // Creator can edit their own code
    return currentUser.id === code.created_by_id;
  };

  // Get unique values for filters
  const uniqueProducts = [...new Set(codes.map(c => c.code_product_name).filter(Boolean))];

  // Filter codes
  const filteredCodes = codes.filter(code => {
    const matchesSearch = !searchTerm || 
      code.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      code.specs?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesProduct = filterProduct === 'all' || code.code_product_name === filterProduct;
    
    return matchesSearch && matchesProduct;
  });

  // Open generator panel
  const openGenerator = async () => {
    await fetchProducts();
    setSelectedProduct(null);
    setAttributeSelections({});
    setAttributeSearchTerms({});
    setPreviewCode('');
    setPreviewSpecs('');
    setIsDuplicate(false);
    setGeneratorOpen(true);
  };

  // Select product and load its attribute values
  const selectProduct = async (product) => {
    setSelectedProduct(product);
    setAttributeSelections({});
    setAttributeSearchTerms({});
    setPreviewCode('');
    setPreviewSpecs('');
    setIsDuplicate(false);

    // Fetch values for each attribute in the product's sequence
    const valuesMap = {};
    for (const attrKey of product.attribute_sequence || []) {
      try {
        const response = await axios.get(
          `${API_URL}/api/item-code-generator/attributes/${attrKey}/values?is_active=true`,
          { headers: getAuthHeader() }
        );
        valuesMap[attrKey] = response.data || [];
      } catch (e) {
        valuesMap[attrKey] = [];
      }
    }
    setAttributeValues(valuesMap);
  };

  // Get attribute definition
  const [attributeDefs, setAttributeDefs] = useState({});
  
  useEffect(() => {
    const fetchAttributeDefs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/item-code-generator/attributes`, { headers: getAuthHeader() });
        const defsMap = {};
        for (const attr of response.data || []) {
          defsMap[attr.attribute_key] = attr;
        }
        setAttributeDefs(defsMap);
      } catch (e) {
        console.error('Error fetching attribute defs:', e);
      }
    };
    fetchAttributeDefs();
  }, []);

  // Update selection
  const updateSelection = (attrKey, value) => {
    const attr = attributeDefs[attrKey];
    
    if (attr?.allow_multiple) {
      // Multi-select: toggle value in array
      const current = attributeSelections[attrKey] || [];
      let newSelection;
      if (current.includes(value)) {
        newSelection = current.filter(v => v !== value);
      } else {
        // Check max selections
        if (attr.max_selections && current.length >= attr.max_selections) {
          toast.error(`Maximum ${attr.max_selections} selections allowed`);
          return;
        }
        newSelection = [...current, value];
      }
      setAttributeSelections({ ...attributeSelections, [attrKey]: newSelection.length > 0 ? newSelection : undefined });
    } else {
      // Single select
      setAttributeSelections({ ...attributeSelections, [attrKey]: value || undefined });
    }
  };

  // Preview code when selections change - debounced to prevent excessive API calls
  useEffect(() => {
    const generatePreview = async () => {
      if (!selectedProduct || Object.keys(attributeSelections).length === 0) {
        setPreviewCode('');
        setPreviewSpecs('');
        return;
      }

      // Check if at least one attribute is selected
      const hasSelection = Object.values(attributeSelections).some(v => 
        v && (Array.isArray(v) ? v.length > 0 : true)
      );
      if (!hasSelection) return;

      try {
        const response = await axios.post(`${API_URL}/api/item-code-generator/preview`, {
          product_id: selectedProduct.id,
          attribute_selections: attributeSelections
        }, { headers: getAuthHeader() });

        setPreviewCode(response.data.code || '');
        setPreviewSpecs(response.data.specs || '');
        setIsDuplicate(response.data.is_duplicate || false);
      } catch (error) {
        console.error('Preview error:', error);
      }
    };

    // Debounce: wait 300ms after last change before making API call
    const timer = setTimeout(() => {
      generatePreview();
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedProduct, attributeSelections]);

  // Generate and save code
  const generateCode = async () => {
    if (!selectedProduct) return toast.error('Please select a product');
    
    const hasSelection = Object.values(attributeSelections).some(v => 
      v && (Array.isArray(v) ? v.length > 0 : true)
    );
    if (!hasSelection) return toast.error('Please select at least one attribute value');

    setGenerating(true);
    try {
      const response = await axios.post(`${API_URL}/api/item-code-generator/generate`, {
        product_id: selectedProduct.id,
        attribute_selections: attributeSelections
      }, { headers: getAuthHeader() });

      if (response.data.is_duplicate) {
        toast.info('This code already exists');
      } else {
        toast.success('Item code generated successfully!');
      }

      setGeneratorOpen(false);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate code');
    } finally {
      setGenerating(false);
    }
  };

  // Copy code to clipboard
  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  // View code details
  const viewCode = (code) => {
    setViewingCode(code);
    setViewDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (code) => {
    setEditingCode(code);
    setEditForm({
      code: code.code || '',
      generic_name: code.generic_name || '',
      specs: code.specs || ''
    });
    setEditDialogOpen(true);
  };

  // Save edited code
  const handleSaveEdit = async () => {
    if (!editingCode) return;
    
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/item-code-generator/codes/${editingCode.id}`,
        editForm,
        { headers: getAuthHeader() }
      );
      toast.success('Code updated successfully');
      setEditDialogOpen(false);
      setEditingCode(null);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update code');
    } finally {
      setSaving(false);
    }
  };

  // Delete code
  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Are you sure you want to delete code "${code.code}"?`)) return;
    
    setDeleting(code.id);
    try {
      await axios.delete(
        `${API_URL}/api/item-code-generator/codes/${code.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('Code deleted successfully');
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete code');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="item-code-manager-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Library className="w-6 h-6" />
              Item Code Manager
            </h1>
            <p className="text-muted-foreground">Generate new item codes and browse the code library</p>
          </div>
          <Button onClick={openGenerator} data-testid="generate-code-btn">
            <Plus className="w-4 h-4 mr-2" />
            Generate New Code
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search codes, specs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="code-search"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <Select value={filterProduct} onValueChange={setFilterProduct}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="All Products" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {uniqueProducts.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Codes Library Table */}
        <Card>
          <CardHeader>
            <CardTitle>Generated Codes</CardTitle>
            <CardDescription>
              {filteredCodes.length} code{filteredCodes.length !== 1 ? 's' : ''} found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filteredCodes.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {searchTerm || filterProduct !== 'all'
                  ? 'No codes match your filters' 
                  : 'No codes generated yet. Click "Generate New Code" to create your first item code.'}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">Item Code</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead className="w-32">Created</TableHead>
                    <TableHead className="w-36 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCodes.map((code) => (
                    <TableRow key={code.id} data-testid={`code-row-${code.id}`}>
                      <TableCell>
                        <code className="font-mono text-sm font-semibold text-primary">{code.code}</code>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{code.code_product_name || '-'}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {code.created_at ? new Date(code.created_at).toLocaleDateString('en-GB') : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => copyCode(code.code)} title="Copy">
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => viewCode(code)} title="View Details">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canEditCode(code) && (
                            <>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => openEditDialog(code)} 
                                title="Edit"
                                data-testid={`edit-code-btn-${code.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDeleteCode(code)} 
                                title="Delete"
                                disabled={deleting === code.id}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-code-btn-${code.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Generator Modal - Two-Panel Layout with Fixed Preview */}
        <Dialog open={generatorOpen} onOpenChange={setGeneratorOpen}>
          <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0 overflow-hidden">
            <DialogHeader className="px-4 pt-4 pb-2 border-b flex-shrink-0">
              <DialogTitle className="flex items-center gap-2 text-base">
                <Sparkles className="w-4 h-4" />
                Generate Item Code
              </DialogTitle>
            </DialogHeader>

            {/* Fixed Live Preview - Always Visible */}
            <div className="px-4 py-2 border-b bg-muted/30 flex-shrink-0">
              {previewCode ? (
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="flex items-center gap-1.5 text-primary text-xs font-medium">
                      <Eye className="w-3.5 h-3.5" />
                      Live Preview
                    </Label>
                    {isDuplicate && (
                      <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs py-0">
                        Already Exists
                      </Badge>
                    )}
                  </div>
                  <p className="font-mono font-bold text-lg text-primary">{previewCode}</p>
                  {previewSpecs && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{previewSpecs}</p>
                  )}
                </div>
              ) : (
                <div className="p-3 bg-background border border-dashed rounded-md text-center">
                  <p className="text-xs text-muted-foreground">Select product and attributes to see preview</p>
                </div>
              )}
            </div>

            {/* Two-Panel Layout */}
            <div className="flex-1 flex overflow-hidden">
              {/* Left Panel: Select Product */}
              <div className="w-1/3 border-r flex flex-col">
                <div className="px-3 py-2 bg-muted/20 border-b flex-shrink-0">
                  <Label className="flex items-center gap-1.5 text-xs font-medium">
                    <Badge variant="outline" className="h-4 w-4 p-0 flex items-center justify-center text-[10px]">1</Badge>
                    Select Product
                  </Label>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  <div className="space-y-1">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className={`p-2 border rounded cursor-pointer transition-colors ${
                          selectedProduct?.id === product.id 
                            ? 'border-primary bg-primary/5' 
                            : 'hover:border-primary/50 hover:bg-muted/30'
                        }`}
                        onClick={() => selectProduct(product)}
                        data-testid={`product-option-${product.code_prefix}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Package className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                            <span className="font-medium text-sm truncate">{product.name}</span>
                          </div>
                          {selectedProduct?.id === product.id && <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />}
                        </div>
                        <div className="flex items-center gap-2 mt-1 ml-5">
                          <Badge variant="secondary" className="font-mono text-[10px] py-0 h-4">{product.code_prefix}</Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {(product.attribute_sequence || []).length} attr
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Panel: Configure Attributes */}
              <div className="w-2/3 flex flex-col">
                <div className="px-3 py-2 bg-muted/20 border-b flex-shrink-0">
                  <Label className="flex items-center gap-1.5 text-xs font-medium">
                    <Badge variant="outline" className="h-4 w-4 p-0 flex items-center justify-center text-[10px]">2</Badge>
                    Configure Attributes
                    {selectedProduct && (
                      <span className="text-muted-foreground font-normal ml-1">
                        — {selectedProduct.name}
                      </span>
                    )}
                  </Label>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {!selectedProduct ? (
                    <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                      <div className="text-center">
                        <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p>Select a product to configure attributes</p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {(selectedProduct.attribute_sequence || []).map((attrKey) => {
                        const attr = attributeDefs[attrKey];
                        const values = attributeValues[attrKey] || [];
                        const currentSelection = attributeSelections[attrKey];
                        const searchTerm = attributeSearchTerms[attrKey] || '';
                        
                        // Filter values based on search
                        const filteredValues = values.filter(val => 
                          val.value_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          val.value_code?.toLowerCase().includes(searchTerm.toLowerCase())
                        );

                        return (
                          <div key={attrKey} className={`p-2 border rounded bg-background ${attr?.allow_multiple ? 'col-span-2' : ''}`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs font-medium">{attr?.attribute_name || attrKey}</span>
                              <div className="flex items-center gap-1">
                                {attr?.allow_multiple && (
                                  <Badge variant="outline" className="text-[10px] py-0 h-4">
                                    Multi {attr.max_selections ? `(${attr.max_selections})` : ''}
                                  </Badge>
                                )}
                                <Badge variant="secondary" className="text-[10px] py-0 h-4">
                                  {values.length}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Search input for attributes with many values */}
                            {values.length > 8 && (
                              <div className="relative mb-1.5">
                                <Search className="absolute left-2 top-1.5 h-3 w-3 text-muted-foreground" />
                                <Input
                                  placeholder={`Search...`}
                                  value={searchTerm}
                                  onChange={(e) => setAttributeSearchTerms(prev => ({
                                    ...prev,
                                    [attrKey]: e.target.value
                                  }))}
                                  className="pl-6 h-7 text-xs"
                                />
                                {searchTerm && (
                                  <button
                                    onClick={() => setAttributeSearchTerms(prev => ({
                                      ...prev,
                                      [attrKey]: ''
                                    }))}
                                    className="absolute right-2 top-1.5"
                                  >
                                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                                  </button>
                                )}
                              </div>
                            )}
                            
                            {attr?.allow_multiple ? (
                              // Multi-select: show as compact chips
                              <div className="max-h-24 overflow-y-auto">
                                <div className="flex flex-wrap gap-1">
                                  {filteredValues.map((val) => {
                                    const isSelected = (currentSelection || []).includes(val.value_code);
                                    return (
                                      <button
                                        key={val.id}
                                        type="button"
                                        className={`px-2 py-0.5 text-xs border rounded-full transition-colors ${
                                          isSelected 
                                            ? 'bg-primary text-primary-foreground border-primary' 
                                            : 'hover:border-primary/50'
                                        }`}
                                        onClick={() => updateSelection(attrKey, val.value_code)}
                                      >
                                        {val.value_name}
                                      </button>
                                    );
                                  })}
                                  {filteredValues.length === 0 && searchTerm && (
                                    <p className="text-xs text-muted-foreground py-1">No match</p>
                                  )}
                                </div>
                              </div>
                            ) : (
                              // Single select: compact dropdown
                              <Select
                                value={currentSelection || ''}
                                onValueChange={(value) => updateSelection(attrKey, value)}
                              >
                                <SelectTrigger className="h-8 text-xs" data-testid={`select-${attrKey}`}>
                                  <SelectValue placeholder={`Select...`} />
                                </SelectTrigger>
                                <SelectContent>
                                  {filteredValues.map((val) => (
                                    <SelectItem key={val.id} value={val.value_code} className="text-xs">
                                      {val.value_name}
                                    </SelectItem>
                                  ))}
                                  {filteredValues.length === 0 && searchTerm && (
                                    <div className="px-2 py-2 text-xs text-muted-foreground text-center">
                                      No match
                                    </div>
                                  )}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Compact Footer */}
            <DialogFooter className="px-4 py-2 border-t flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => setGeneratorOpen(false)}>Cancel</Button>
              <Button 
                size="sm"
                onClick={generateCode} 
                disabled={generating || !selectedProduct}
                data-testid="confirm-generate-btn"
              >
                {generating ? 'Generating...' : 'Generate & Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Code Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Code Details
              </DialogTitle>
            </DialogHeader>
            {viewingCode && (
              <div className="space-y-4 py-4">
                <div>
                  <span className="text-xs text-muted-foreground uppercase">Item Code</span>
                  <p className="font-mono font-bold text-xl text-primary">{viewingCode.code}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Product</span>
                    <p className="font-medium">{viewingCode.code_product_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Classification</span>
                    <p className="font-medium">{viewingCode.classification || '-'}</p>
                  </div>
                </div>
                {viewingCode.specs && (
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Specifications</span>
                    <p className="text-sm">{viewingCode.specs}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Created By</span>
                    <p className="text-sm">{viewingCode.created_by_name || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground uppercase">Created At</span>
                    <p className="text-sm">
                      {viewingCode.created_at ? new Date(viewingCode.created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => copyCode(viewingCode?.code)}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Code
              </Button>
              <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Code Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5" />
                Edit Item Code
              </DialogTitle>
              <DialogDescription>
                Modify the code details. Changes are blocked if code is used in transactions.
              </DialogDescription>
            </DialogHeader>
            {editingCode && (
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit-code">Item Code *</Label>
                  <Input
                    id="edit-code"
                    value={editForm.code}
                    onChange={(e) => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
                    placeholder="Enter item code"
                    data-testid="edit-code-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-generic-name">Generic Name</Label>
                  <Input
                    id="edit-generic-name"
                    value={editForm.generic_name}
                    onChange={(e) => setEditForm({ ...editForm, generic_name: e.target.value })}
                    placeholder="Enter generic name"
                    data-testid="edit-generic-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-specs">Specifications</Label>
                  <Textarea
                    id="edit-specs"
                    value={editForm.specs}
                    onChange={(e) => setEditForm({ ...editForm, specs: e.target.value.toUpperCase() })}
                    placeholder="Enter specifications"
                    rows={3}
                    data-testid="edit-specs-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleSaveEdit} 
                disabled={saving || !editForm.code.trim()}
                data-testid="save-edit-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
